import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const EquipmentInventory = ({ equipment, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const lifecycleStatusOptions = [
    { value: 'all', label: 'All Lifecycle Stages' },
    { value: 'procurement', label: 'Procurement' },
    { value: 'installation', label: 'Installation' },
    { value: 'active', label: 'Active Use' },
    { value: 'maintenance_required', label: 'Maintenance Required' },
    { value: 'end_of_life', label: 'End of Life' },
    { value: 'disposed', label: 'Disposed' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'operational', label: 'Operational' },
    { value: 'maintenance', label: 'In Maintenance' },
    { value: 'out-of-service', label: 'Out of Service' },
    { value: 'retired', label: 'Retired' }
  ];

  const filteredEquipment = equipment?.filter((eq) => {
    const matchesSearch = searchTerm === '' ||
      eq?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      eq?.barcode?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      eq?.serial_number?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesStatus = statusFilter === 'all' || eq?.status === statusFilter;
    const matchesLifecycle = lifecycleFilter === 'all' || eq?.lifecycle_status === lifecycleFilter;

    return matchesSearch && matchesStatus && matchesLifecycle;
  });

  const getLifecycleBadge = (status) => {
    const config = {
      procurement: { label: 'Procurement', color: 'text-blue-600', bg: 'bg-blue-100' },
      installation: { label: 'Installation', color: 'text-purple-600', bg: 'bg-purple-100' },
      active: { label: 'Active', color: 'text-success', bg: 'bg-success/10' },
      maintenance_required: { label: 'Maintenance Required', color: 'text-warning', bg: 'bg-warning/10' },
      end_of_life: { label: 'End of Life', color: 'text-error', bg: 'bg-error/10' },
      disposed: { label: 'Disposed', color: 'text-muted-foreground', bg: 'bg-muted' }
    };
    const cfg = config?.[status] || config?.active;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      operational: { label: 'Operational', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
      maintenance: { label: 'Maintenance', color: 'text-warning', bg: 'bg-warning/10', icon: 'Wrench' },
      'out-of-service': { label: 'Out of Service', color: 'text-error', bg: 'bg-error/10', icon: 'AlertCircle' },
      retired: { label: 'Retired', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Archive' }
    };
    const cfg = config?.[status] || config?.operational;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-xl p-4 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            type="search"
            placeholder="Search by name, barcode, serial number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
          <Select
            options={lifecycleStatusOptions}
            value={lifecycleFilter}
            onChange={setLifecycleFilter}
            placeholder="Lifecycle Stage"
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Operational Status"
          />
        </div>
      </div>

      {/* Equipment Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredEquipment?.map((eq) => (
          <div key={eq?.id} className="bg-card rounded-xl border border-border hover:shadow-md transition-shadow">
            <div className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <Image
                    src={eq?.image_url}
                    alt={`${eq?.name} equipment image`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground mb-1">{eq?.name}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{eq?.manufacturer} {eq?.model}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {getLifecycleBadge(eq?.lifecycle_status)}
                    {getStatusBadge(eq?.status)}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="text-sm font-medium text-foreground">{eq?.location}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Barcode</p>
                  <p className="text-sm font-medium text-foreground">{eq?.barcode || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Purchase Cost</p>
                  <p className="text-sm font-medium text-foreground">{formatCurrency(eq?.purchase_cost)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Remaining Life</p>
                  <p className="text-sm font-medium text-foreground">
                    {eq?.remaining_useful_life_years?.toFixed(1)} years
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Next Maintenance: {eq?.next_maintenance_date ? new Date(eq?.next_maintenance_date)?.toLocaleDateString() : 'Not scheduled'}
                </div>
                <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
                  Details
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEquipment?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Equipment Found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or search criteria</p>
        </div>
      )}
    </div>
  );
};

export default EquipmentInventory;